import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import { useUpdateUserMutation } from "@api/user.api";
import { UserSignupPurpose } from "@common/types/user";
import { UserSchema } from "@common/schemas/user.schema";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { EnterKeyHint } from "@components/onboarding/components/EnterKeyHint";
import animations from "../../layout/OnboardingLayout.module.scss";
import styles from "./Purpose.module.scss";

interface Props {
    onContinue: (updatedUser?: UserSchema) => void;
}

export const Purpose: React.FC<Props> = ({ onContinue }) => {
    const updateUserMutation = useUpdateUserMutation();

    const handleSelect = async (purpose: UserSignupPurpose) => {
        const updatedUser = await updateUserMutation.mutateAsync({
            signupPurpose: purpose,
        });
        onContinue(updatedUser);
    };

    useKeyboardCommand({
        keys: "enter",
        callback: () => {
            handleSelect(UserSignupPurpose.BUSINESS_OWNER);
        },
        options: {
            description: "Continue",
        },
    });

    return (
        <div className={classNames(styles.container, animations.fadeIn1)}>
            <div
                className={classNames(styles.titleSection, animations.fadeIn2)}
            >
                <div className={styles.title}>Get started with Kick</div>
                <div className={styles.subtitle}>
                    Select the type of account to create
                </div>
            </div>
            <div className={styles.options}>
                <WithHotkey
                    hotKey="enter"
                    hotKeyOptions={{
                        description: "Continue as Business owner",
                    }}
                    hotKeyDisplayPosition="none"
                >
                    <Button
                        onClick={() =>
                            handleSelect(UserSignupPurpose.BUSINESS_OWNER)
                        }
                        variant="primary"
                        size="lg"
                        className={styles.content}
                        block
                        rightIcon={
                            <EnterKeyHint className={styles.hideOnMobile} />
                        }
                    >
                        <div className={styles.contentContainer}>
                            <div className={styles.title}>Business owner</div>
                            <div className={styles.description}>
                                Set up accounting for your business
                            </div>
                        </div>
                    </Button>
                </WithHotkey>
                <Button
                    onClick={() => handleSelect(UserSignupPurpose.ACCOUNTANT)}
                    variant="primary"
                    size="lg"
                    className={styles.content}
                    block
                >
                    <div className={styles.contentContainer}>
                        <div className={styles.title}>Accounting firm</div>
                        <div className={styles.description}>
                            Set up your firm and add your first client
                        </div>
                    </div>
                </Button>
            </div>
        </div>
    );
};
